export interface ModelConfig {
  id: string;
  name: string;
  provider: 'openai' | 'anthropic' | 'google' | 'meta' | 'mistral' | 'stability' | 'flux' | 'midjourney' | 'ollama' | 'local';
  type: 'text' | 'image' | 'multimodal';
  isAvailable: boolean;
  maxTokens?: number;
  temperature?: number;
  isDynamic?: boolean; // For models discovered at runtime
}

export const AVAILABLE_MODELS: ModelConfig[] = [
  // OpenAI Models
  {
    id: 'gpt-4-turbo',
    name: 'GPT-4 Turbo',
    provider: 'openai',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gpt-4',
    name: 'GPT-4',
    provider: 'openai',
    type: 'text',
    isAvailable: true,
    maxTokens: 8192,
    temperature: 0.7,
  },
  {
    id: 'gpt-4o',
    name: 'GPT-4o',
    provider: 'openai',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gpt-4o-mini',
    name: 'GPT-4o Mini',
    provider: 'openai',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gpt-3.5-turbo',
    name: 'GPT-3.5 Turbo',
    provider: 'openai',
    type: 'text',
    isAvailable: true,
    maxTokens: 16385,
    temperature: 0.7,
  },
  // Anthropic Claude Models
  {
    id: 'claude-3-opus',
    name: 'Claude 3 Opus',
    provider: 'anthropic',
    type: 'text',
    isAvailable: true,
    maxTokens: 200000,
    temperature: 0.7,
  },
  {
    id: 'claude-3-sonnet',
    name: 'Claude 3 Sonnet',
    provider: 'anthropic',
    type: 'text',
    isAvailable: true,
    maxTokens: 200000,
    temperature: 0.7,
  },
  {
    id: 'claude-3-haiku',
    name: 'Claude 3 Haiku',
    provider: 'anthropic',
    type: 'text',
    isAvailable: true,
    maxTokens: 200000,
    temperature: 0.7,
  },
  {
    id: 'claude-3.5-sonnet',
    name: 'Claude 3.5 Sonnet',
    provider: 'anthropic',
    type: 'text',
    isAvailable: true,
    maxTokens: 200000,
    temperature: 0.7,
  },
  // Google Gemini Models
  {
    id: 'gemini-2.0-flash',
    name: 'Gemini 2.0 Flash',
    provider: 'google',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Gemini 1.5 Pro',
    provider: 'google',
    type: 'text',
    isAvailable: true,
    maxTokens: 1048576,
    temperature: 0.7,
  },
  {
    id: 'gemini-1.5-flash',
    name: 'Gemini 1.5 Flash',
    provider: 'google',
    type: 'text',
    isAvailable: true,
    maxTokens: 1048576,
    temperature: 0.7,
  },
  {
    id: 'gemini-pro',
    name: 'Gemini Pro',
    provider: 'google',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Meta Models
  {
    id: 'llama-3.1-405b',
    name: 'Llama 3.1 405B',
    provider: 'meta',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'llama-3.1-70b',
    name: 'Llama 3.1 70B',
    provider: 'meta',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'llama-3.1-8b',
    name: 'Llama 3.1 8B',
    provider: 'meta',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  // Mistral Models
  {
    id: 'mistral-large',
    name: 'Mistral Large',
    provider: 'mistral',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'mistral-medium',
    name: 'Mistral Medium',
    provider: 'mistral',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'mixtral-8x7b',
    name: 'Mixtral 8x7B',
    provider: 'mistral',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Ollama/Local Models - Latest 2024-2025 models
  // DeepSeek Models (Best for reasoning and coding)
  {
    id: 'deepseek-r1:8b',
    name: 'DeepSeek-R1 8B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'deepseek-r1:32b',
    name: 'DeepSeek-R1 32B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'deepseek-r1:70b',
    name: 'DeepSeek-R1 70B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'deepseek-coder:6.7b',
    name: 'DeepSeek Coder 6.7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 16384,
    temperature: 0.7,
  },
  // Qwen 3 Models (Latest generation)
  {
    id: 'qwen3:8b',
    name: 'Qwen 3 8B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'qwen3:14b',
    name: 'Qwen 3 14B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'qwen3:30b',
    name: 'Qwen 3 30B (MoE)',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 40960,
    temperature: 0.7,
  },
  {
    id: 'qwen3:32b',
    name: 'Qwen 3 32B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'qwen3:72b',
    name: 'Qwen 3 72B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'qwen3-coder:14b',
    name: 'Qwen 3 Coder 14B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'qwq:32b',
    name: 'QwQ 32B (Reasoning)',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Gemma 3 Models (Google - Latest)
  {
    id: 'gemma3:270m',
    name: 'Gemma 3 270M',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gemma3:1b',
    name: 'Gemma 3 1B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gemma3:4b',
    name: 'Gemma 3 4B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gemma3:12b',
    name: 'Gemma 3 12B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'gemma3:27b',
    name: 'Gemma 3 27B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  // GPT-OSS Models (Open Source GPT)
  {
    id: 'gpt-oss:20b',
    name: 'GPT-OSS 20B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'gpt-oss:120b',
    name: 'GPT-OSS 120B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Phi Models (Microsoft)
  {
    id: 'phi-4:14b',
    name: 'Phi-4 14B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 16384,
    temperature: 0.7,
  },
  {
    id: 'phi-3:3.8b',
    name: 'Phi-3 3.8B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  {
    id: 'phi-3:14b',
    name: 'Phi-3 14B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  // Mistral Models
  {
    id: 'mistral:7b',
    name: 'Mistral 7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'mixtral:8x7b',
    name: 'Mixtral 8x7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'mixtral:8x22b',
    name: 'Mixtral 8x22B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 65536,
    temperature: 0.7,
  },
  // Specialized Models
  {
    id: 'codestral:22b',
    name: 'Codestral 22B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'starcoder2:15b',
    name: 'StarCoder2 15B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 16384,
    temperature: 0.7,
  },
  {
    id: 'solar:10.7b',
    name: 'Solar 10.7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  {
    id: 'yi:34b',
    name: 'Yi 34B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  // Command-R Models (Cohere)
  {
    id: 'command-r:35b',
    name: 'Command-R 35B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  {
    id: 'command-r-plus:104b',
    name: 'Command-R Plus 104B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 128000,
    temperature: 0.7,
  },
  // Dolphin Models (Uncensored)
  {
    id: 'dolphin-mixtral:8x7b',
    name: 'Dolphin Mixtral 8x7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Nous Hermes Models
  {
    id: 'nous-hermes2:34b',
    name: 'Nous Hermes 2 34B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  {
    id: 'nous-hermes2-mixtral:8x7b',
    name: 'Nous Hermes 2 Mixtral 8x7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Orca Models
  {
    id: 'orca2:13b',
    name: 'Orca 2 13B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
  },
  // Vicuna Models
  {
    id: 'vicuna:13b',
    name: 'Vicuna 13B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 2048,
    temperature: 0.7,
  },
  // WizardLM Models
  {
    id: 'wizardlm2:7b',
    name: 'WizardLM 2 7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  {
    id: 'wizardlm2:8x22b',
    name: 'WizardLM 2 8x22B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 65536,
    temperature: 0.7,
  },
  // Zephyr Model
  {
    id: 'zephyr:7b',
    name: 'Zephyr 7B',
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 32768,
    temperature: 0.7,
  },
  // Image Models
  {
    id: 'dall-e-3',
    name: 'DALL-E',
    provider: 'openai',
    type: 'image',
    isAvailable: true,
  },
  {
    id: 'midjourney-v6',
    name: 'Midjourney',
    provider: 'midjourney',
    type: 'image',
    isAvailable: true,
  },
  {
    id: 'stable-diffusion-xl',
    name: 'Stable Diffusion',
    provider: 'stability',
    type: 'image',
    isAvailable: true,
  },
  {
    id: 'flux-pro',
    name: 'Flux',
    provider: 'flux',
    type: 'image',
    isAvailable: true,
  },
];

export const getAvailableModels = (type?: 'text' | 'image' | 'multimodal'): ModelConfig[] => {
  let models = AVAILABLE_MODELS.filter(model => model.isAvailable);
  if (type) {
    models = models.filter(model => model.type === type);
  }
  return models;
};

export const getModelById = (id: string): ModelConfig | undefined => {
  return AVAILABLE_MODELS.find(model => model.id === id);
};

export const getDefaultTextModel = (): string => {
  // Default to Gemma 3 27B for Ollama, fallback to GPT-4 Turbo for cloud
  return 'gemma3:27b';
};

export const getDefaultImageModel = (): string => {
  return 'flux-pro';
};

export const getOllamaModels = (): ModelConfig[] => {
  return AVAILABLE_MODELS.filter(model => model.provider === 'ollama' || model.provider === 'local');
};

export const isOllamaProvider = (provider: string): boolean => {
  return provider === 'ollama' || provider === 'local';
};

// Helper to create a ModelConfig for custom Ollama models
export const createOllamaModelConfig = (modelId: string, displayName?: string): ModelConfig => {
  return {
    id: modelId,
    name: displayName || `${modelId} (Local)`,
    provider: 'ollama',
    type: 'text',
    isAvailable: true,
    maxTokens: 4096,
    temperature: 0.7,
    isDynamic: true
  };
};

